



































import { defineComponent, nextTick, PropType, ref } from '@vue/composition-api';

export interface SegmentedTabOption {
    key: string;
    label: string;
}

export default defineComponent({
    name: 'TailwindSegmentedTabs',
    model: { prop: 'value', event: 'change' },
    props: {
        value: { type: String, default: null },
        options: { type: Array as PropType<SegmentedTabOption[]>, default: () => [] },
        /** Accessible name of the group, e.g. "Ingestion job scope" */
        ariaLabel: { type: String, required: true },
        /** id of the element with role="tabpanel" that these tabs control */
        panelId: { type: String, required: true },
        /** prefix for generated ids and data-testid attributes */
        idPrefix: { type: String, default: 'segmented' },
        disabled: { type: Boolean, default: false },
        fullWidthOnMobile: { type: Boolean, default: true },
    },
    setup(props, { emit }) {
        const container = ref<HTMLElement | null>(null);
        const focusedKey = ref<string | null>(null);

        const tabId = (key: string) => `${props.idPrefix}-tab-${key}`;

        const select = (key: string) => {
            focusedKey.value = key;
            if (key !== props.value) emit('change', key);
        };

        const focusTab = (index: number) => {
            const option = props.options[index];
            if (!option) return;
            focusedKey.value = option.key;
            nextTick(() => {
                const buttons = container.value?.querySelectorAll<HTMLButtonElement>('[role="tab"]');
                if (buttons && buttons[index]) buttons[index].focus();
            });
        };

        /**
         * Manual-activation tablist: arrows/Home/End move focus only, Enter/Space (native
         * button activation) commits, so arrowing through does not fire a search per keypress.
         */
        const onKeydown = (event: KeyboardEvent) => {
            const keys = props.options.map((option: SegmentedTabOption) => option.key);
            if (!keys.length) return;
            const from = keys.indexOf(focusedKey.value || props.value);
            const current = from === -1 ? 0 : from;
            let next: number;
            switch (event.key) {
                case 'ArrowRight':
                case 'ArrowDown':
                    next = (current + 1) % keys.length;
                    break;
                case 'ArrowLeft':
                case 'ArrowUp':
                    next = (current - 1 + keys.length) % keys.length;
                    break;
                case 'Home':
                    next = 0;
                    break;
                case 'End':
                    next = keys.length - 1;
                    break;
                default:
                    return;
            }
            event.preventDefault();
            focusTab(next);
        };

        return { container, focusedKey, tabId, select, onKeydown };
    },
});
